import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * State and LGA, picked from the real list.
 *
 * ── WHY THIS IS SHARED AND NOT COPIED ───────────────────────────────────────
 *
 * Three forms ask where somewhere is: pharmacy registration, address entry and
 * delivery zones. They only work together if they offer the SAME list — a zone
 * priced for "Abuja Municipal" and a pharmacy registered in "Wuse" never match,
 * and the failure is silent. One component, one source, one spelling.
 *
 * ── WHY THE LGA IS NOT THE DISTRICT ─────────────────────────────────────────
 *
 * People say "Wuse", "Garki", "Lekki" — districts, not LGAs. Wuse and Garki are
 * inside Abuja Municipal; Lekki is in Eti-Osa. Both are right in conversation
 * and wrong in this field, because delivery is matched on the LGA. The hint
 * says so, and the street line is where the district belongs.
 */
export function LocationFields({
  state,
  lga,
  onState,
  onLga,
  stateError,
  lgaError,
  lgaOptional = false,
}: {
  state: string;
  lga: string;
  onState: (value: string) => void;
  onLga: (value: string) => void;
  stateError?: string;
  lgaError?: string;
  lgaOptional?: boolean;
}) {
  const { data } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) => api.locations(signal),
    // It changes roughly never.
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const states = data?.states ?? [];
  const lgas = data?.lgas[state] ?? [];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">State</span>
        <select
          value={state}
          onChange={(event) => {
            // The LGAs of the old state mean nothing under the new one.
            onState(event.target.value);
            onLga('');
          }}
          className={cn(
            'min-h-12 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
            stateError ? 'border-danger' : 'border-line',
          )}
        >
          <option value="">Choose a state</option>
          {states.map((one) => (
            <option key={one} value={one}>
              {one}
            </option>
          ))}
        </select>
        {stateError ? (
          <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
            {stateError}
          </span>
        ) : null}
      </label>

      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          LGA{lgaOptional ? <span className="font-normal text-faint"> (optional)</span> : null}
        </span>
        <select
          value={lga}
          disabled={!state}
          onChange={(event) => onLga(event.target.value)}
          className={cn(
            'min-h-12 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
            lgaError ? 'border-danger' : 'border-line',
            !state && 'opacity-60',
          )}
        >
          <option value="">{state ? 'Choose an LGA' : 'Pick a state first'}</option>
          {lgas.map((one) => (
            <option key={one} value={one}>
              {one}
            </option>
          ))}
        </select>
        {lgaError ? (
          <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
            {lgaError}
          </span>
        ) : state ? (
          <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">
            The local government area, not the district. Wuse and Garki are in Abuja Municipal;
            Lekki is in Eti-Osa. Put the district in the address above.
          </span>
        ) : null}
      </label>
    </div>
  );
}
